import { createReducer, on } from "@ngrx/store";
import * as BookingActions from "./booking.actions";
import { initialBookingState, BookingState } from "./booking.state";
import {
  ActiveWorkshopSystemResponse,
  CalendarDayTimeSlotResponse,
  CampaignResponse,
  DealerResponse,
  ExternalTyreUrlResponse,
  GetTechUpdateStatusResponse,
  InsuranceCompanyResponse,
  PackageResponse,
  PackageTypeEnum,
  VehicleSearchResponse,
  VehicleWarningLightsResponse,
  WorkshopSystemEnum
} from "src/domain/client";
import { IActiveWorkshopSystemResponseBusinessProposal } from "../classes/active-workshop-system-response-business-proposal";
import { PageFormIdEnum } from "../classes/enums";
import {
  DiscountCodeValidationStateEnum,
  SelectedPackages
} from "./utils/booking.modals";

export const bookingReducer = createReducer(
  initialBookingState,

  // --- Step 1 ---
  on(
    BookingActions.submitAboutVehicle,
    (state, { chassiNumber, odometer, searchedByRegno }): BookingState => ({
      ...state,
      isLoading: true,
      error: null,
      chassiNumber,
      odometer,
      searchedByRegno
    })
  ),

  on(BookingActions.loadCarInfoSuccess, (state, { carInfo }): BookingState => ({
    ...state,
    isLoading: false,
    carInfo
  })),

  on(
    BookingActions.loadCarInfoFailure,
    (state, { error, carInfo }): BookingState => ({
      ...state,
      isLoading: false,
      error,
      carInfo
    })
  ),

  on(BookingActions.updateOdometer, (state, { odometer }): BookingState => ({
    ...state,
    odometer
  })),

  on(
    BookingActions.updateProviderIds,
    (state, { providerIds }): BookingState => ({
      ...state,
      providerIds
    })
  ),

  on(
    BookingActions.loadTechUpdateStatusSuccess,
    (state, { status }): BookingState => ({
      ...state,
      techUpdateStatus: status
    })
  ),

  on(BookingActions.clearCarInfo, (state): BookingState => ({
    ...state,
    carInfo: null,
    techUpdateStatus: null,
    error: null
  })),

  // --- Step 2 ---
  on(BookingActions.loadActiveWorkshopSystems, (state): BookingState => ({
    ...state,
    isLoading: true,
    error: null
  })),

  on(
    BookingActions.loadActiveWorkshopSystemsSuccess,
    (state, { systems }): BookingState => ({
      ...state,
      isLoading: false,
      activeWorkshopSystems: systems
    })
  ),

  on(
    BookingActions.loadActiveWorkshopSystemsFailure,
    (state, { error }): BookingState => ({
      ...state,
      isLoading: false,
      error: error
    })
  ),

  on(
    BookingActions.submitWorkshopSystems,
    (state, { selectedSystems }): BookingState => ({
      ...state,
      selectedWorkshopSystems: selectedSystems
    })
  ),

  // --- Step 3 ---
  on(BookingActions.loadDealers, (state): BookingState => ({
    ...state,
    isLoading: true,
    error: null
  })),

  on(BookingActions.loadDealersSuccess, (state, { dealers }): BookingState => ({
    ...state,
    isLoading: false,
    dealers: dealers
  })),

  on(BookingActions.loadDealersFailure, (state, { error }): BookingState => ({
    ...state,
    isLoading: false,
    error: error
  })),

  on(BookingActions.selectDealer, (state, { dealerNumber }): BookingState => ({
    ...state,
    selectedDealerNumber: dealerNumber,
    isPartialLoading: true
  })),

  on(
    BookingActions.loadEnabledServiceTypesSuccess,
    (state, { types }): BookingState => ({
      ...state,
      enabledServiceTypes: types,
      isPartialLoading: false
    })
  ),

  // --- Step 4 ---
  on(
    BookingActions.loadSpecificationsPageData,
    (state, { isPartialReload }): BookingState => ({
      ...state,
      isLoading: !isPartialReload,
      isPartialLoading: !!isPartialReload,
      error: null
    })
  ),

  on(
    BookingActions.loadSpecificationsPageDataSuccess,
    (
      state,
      {
        basePackages,
        additionalPackages,
        segment,
        urlResponse,
        providers,
        lights
      }
    ): BookingState => ({
      ...state,
      isLoading: false,
      isPartialLoading: false,
      error: null,
      basePackages: basePackages,
      additionalPackages: additionalPackages,
      vehicleSegment: segment,
      externalTyreUrl: urlResponse,
      insuranceProviders: providers,
      warningLights: lights
    })
  ),

  on(
    BookingActions.loadSpecificationsPageDataFailure,
    (state, { error }): BookingState => ({
      ...state,
      isLoading: false,
      isPartialLoading: false,
      error: error
    })
  ),

  on(
    BookingActions.loadBasePackagesSuccess,
    (state, { packages }): BookingState => ({
      ...state,
      isLoading: false,
      basePackages: packages
    })
  ),
  on(
    BookingActions.loadBasePackagesFailure,
    (state, { error }): BookingState => ({
      ...state,
      isLoading: false,
      error: error
    })
  ),

  on(
    BookingActions.loadAdditionalPackagesSuccess,
    (state, { packages }): BookingState => ({
      ...state,
      additionalPackages: packages
    })
  ),
  on(
    BookingActions.loadInsuranceProvidersSuccess,
    (state, { providers }): BookingState => ({
      ...state,
      insuranceProviders: providers
    })
  ),
  on(
    BookingActions.loadWarningLightsSuccess,
    (state, { lights }): BookingState => ({
      ...state,
      warningLights: lights
    })
  ),
  on(
    BookingActions.loadVehicleSegmentSuccess,
    (state, { segment }): BookingState => ({
      ...state,
      vehicleSegment: segment
    })
  ),
  on(
    BookingActions.loadExternalTyreUrlSuccess,
    (state, { urlResponse }): BookingState => ({
      ...state,
      externalTyreUrl: urlResponse
    })
  ),
  on(
    BookingActions.updateSelectedPackages,
    (state, { selectedPackages }): BookingState => {
      const newSelectedPackages = {
        ...(state.selectedPackages || {}),
        ...selectedPackages,
        additionalPackagesForm: {
          ...(state.selectedPackages?.additionalPackagesForm || {}),
          ...(selectedPackages.additionalPackagesForm || {})
        },
        serviceForm: {
          ...(state.selectedPackages?.serviceForm || {}),
          ...(selectedPackages.serviceForm || {})
        },
        troubleshootingForm: {
          ...(state.selectedPackages?.troubleshootingForm || {}),
          ...(selectedPackages.troubleshootingForm || {})
        }
      };

      return {
        ...state,
        selectedPackages: newSelectedPackages
      };
    }
  ),

  on(
    BookingActions.businessProposalLoadedSuccess,
    (state, { proposal }): BookingState => {
      const proposalServices = proposal.packages.reduce<{
        [key: string]: boolean;
      }>((acc, item) => {
        if (
          item.type === PackageTypeEnum.OriginalService ||
          item.type === PackageTypeEnum.BaseService
        )
          acc.Service = true;
        if (item.type === PackageTypeEnum.Troubleshooting)
          acc.Troubleshooting = true;
        if (item.type === PackageTypeEnum.TechUpdate) acc.TechUpdate = true;
        if (item.type === PackageTypeEnum.Wheel) acc.Wheel = true;
        if (item.type === PackageTypeEnum.Windshield) acc.Windscreen = true;
        return acc;
      }, {});

      const selectedSystems = Object.keys(proposalServices).filter(
        (key) => proposalServices[key]
      ) as WorkshopSystemEnum[];

      const activeWorkshopSystems = proposal.workshopSystems.map(
        (item) =>
          new IActiveWorkshopSystemResponseBusinessProposal({
            ...item,
            selectedFromBusinessProposal:
              !!proposalServices[item.workshopSystemId]
          })
      );

      const initialSelectedPackages: SelectedPackages = {};
      const typeToFormNameMap: {
        [key in PackageTypeEnum]?: keyof SelectedPackages;
      } = {
        [PackageTypeEnum.OriginalService]: "serviceForm",
        [PackageTypeEnum.BaseService]: "serviceForm",
        [PackageTypeEnum.Troubleshooting]: "troubleshootingForm",
        [PackageTypeEnum.Wheel]: "wheelForm",
        [PackageTypeEnum.Windshield]: "windscreenForm",
        [PackageTypeEnum.TechUpdate]: "techUpdateForm"
      };
      const additionalPackageTypes = [
        PackageTypeEnum.PPS,
        PackageTypeEnum.WashAndRecond,
        PackageTypeEnum.ServiceAdditional,
        PackageTypeEnum.DealerExtra
      ];

      (proposal.packages || []).forEach((pkg) => {
        const formName = typeToFormNameMap[pkg.type];

        if (formName) {
          if (!initialSelectedPackages[formName]) {
            initialSelectedPackages[formName] = { selectedContainer: pkg.id };
          }
          const subSelections: { [key: string]: boolean } = {};
          pkg.subPackages.forEach((sp) => (subSelections[sp.id] = true));
          initialSelectedPackages[formName][pkg.id] = subSelections;
        } else if (additionalPackageTypes.includes(pkg.type)) {
          if (!initialSelectedPackages.additionalPackagesForm) {
            initialSelectedPackages.additionalPackagesForm = {};
          }
          const subSelections: { [key: string]: boolean } = {};
          pkg.subPackages.forEach((sp) => (subSelections[sp.id] = true));
          initialSelectedPackages.additionalPackagesForm[pkg.id] = {
            selected: true,
            ...subSelections
          };
        }
      });

      return {
        ...state,
        carInfo: proposal.vehicle,
        odometer: Math.round(proposal.mileage / 10),
        dealers: [proposal.dealer],
        selectedDealerNumber: proposal.dealer.number,
        activeWorkshopSystems: activeWorkshopSystems,
        selectedWorkshopSystems: selectedSystems,
        proposalCustomer: proposal.customer,
        selectedPackages: initialSelectedPackages,
        isBusinessProposalActive: true
      };
    }
  ),

  // --- Step 5 ---
  on(BookingActions.loadExtraServicesData, (state): BookingState => ({
    ...state,
    isLoading: true,
    error: null
  })),

  on(
    BookingActions.loadExtraServicesDataSuccess,
    (state, { packages }): BookingState => {
      const packageMap = new Map<string, PackageResponse>();
      (state.additionalPackages || []).forEach((p) => packageMap.set(p.id, p));
      packages.forEach((p) => packageMap.set(p.id, p));

      return {
        ...state,
        isLoading: false,
        additionalPackages: Array.from(packageMap.values())
      };
    }
  ),

  on(
    BookingActions.loadExtraServicesDataFailure,
    (state, { error }): BookingState => ({
      ...state,
      isLoading: false,
      error: error
    })
  ),

  // --- Step 6 ---
  on(BookingActions.loadAvailableTimes, (state): BookingState => ({
    ...state,
    isLoading: true,
    error: null
  })),
  on(
    BookingActions.loadAvailableTimesSuccess,
    (state, { times, packagesUsedForRequest }): BookingState => ({
      ...state,
      isLoading: false,
      availableTimes: times,
      packagesUsedForTimeRequest: packagesUsedForRequest
    })
  ),
  on(
    BookingActions.loadAvailableTimesFailure,
    (state, { error }): BookingState => ({
      ...state,
      isLoading: false,
      error: error
    })
  ),
  on(BookingActions.selectBookingDate, (state, { date }): BookingState => ({
    ...state,
    selectedDate: date,
    selectedTime: null,
    ebuCars: null,
    selectedEbuCarId: null
  })),
  on(BookingActions.selectBookingTime, (state, { time }): BookingState => ({
    ...state,
    selectedTime: time
  })),

  on(
    BookingActions.selectDropoffOption,
    (state, { dropOffId }): BookingState => ({
      ...state,
      selectedDropoffOptionId: dropOffId,
      ebuCars: null,
      selectedEbuCarId: null
    })
  ),

  on(BookingActions.loadEbuCarsSuccess, (state, { cars }): BookingState => ({
    ...state,
    ebuCars: cars
  })),

  on(BookingActions.loadEbuCarsFailure, (state): BookingState => ({
    ...state,
    ebuCars: null
  })),

  on(BookingActions.selectEbuCar, (state, { groupID }): BookingState => ({
    ...state,
    selectedEbuCarId: groupID
  })),

  // --- Step 7 ---
  on(
    BookingActions.updateContactInformation,
    (state, { info }): BookingState => ({
      ...state,
      contactInformation: {
        ...(state.contactInformation || {}),
        ...info
      } as any
    })
  ),

  on(BookingActions.validateCampaignCode, (state): BookingState => ({
    ...state,
    campaignValidationState: DiscountCodeValidationStateEnum.IsValidating
  })),

  on(
    BookingActions.validateCampaignCodeSuccess,
    (state, { response }): BookingState => ({
      ...state,
      campaignValidationState: response
        ? DiscountCodeValidationStateEnum.Validated
        : DiscountCodeValidationStateEnum.ValidationFailed,
      campaignResponse: response
    })
  ),

  on(
    BookingActions.validateCampaignCodeFailure,
    (state, { error }): BookingState => ({
      ...state,
      campaignValidationState: DiscountCodeValidationStateEnum.ValidationFailed,
      error: error
    })
  ),

  on(BookingActions.clearCampaignCode, (state): BookingState => ({
    ...state,
    campaignValidationState: DiscountCodeValidationStateEnum.NotValidated,
    campaignResponse: null
  })),

  // --- Step 8 ---
  on(BookingActions.submitBooking, (state): BookingState => ({
    ...state,
    isLoading: true,
    error: null,
    bookingError: null,
    orderId: null
  })),

  on(
    BookingActions.submitBookingSuccess,
    (state, { orderId }): BookingState => ({
      ...state,
      isLoading: false,
      isBookingMadeSuccessful: true,
      orderId: orderId
    })
  ),

  on(BookingActions.submitBookingFailure, (state, { error }): BookingState => ({
    ...state,
    isLoading: false,
    bookingError: error
  })),

  on(BookingActions.clearBookingResponse, (state): BookingState => ({
    ...state,
    orderId: null,
    bookingError: null,
    isBookingMadeSuccessful: false
  })),

  // --- Shared ---
  on(
    BookingActions.clearStateFromStep,
    (state, { stepNumber }): BookingState => {
      let newState = { ...state };

      if (stepNumber < PageFormIdEnum.workshopSystemSelection) {
        newState = {
          ...newState,
          activeWorkshopSystems: null,
          selectedWorkshopSystems: null
        };
      }

      if (stepNumber < PageFormIdEnum.findDealer) {
        newState = {
          ...newState,
          dealers: null,
          selectedDealerNumber: null,
          enabledServiceTypes: null
        };
      }

      if (stepNumber < PageFormIdEnum.chooseSpecifications) {
        newState = {
          ...newState,
          basePackages: null,
          additionalPackages: null,
          insuranceProviders: null,
          warningLights: null,
          externalTyreUrl: null,
          vehicleSegment: null,
          rejectedIsOnlyTyre: false,
          selectedPackages: null
        };
      }

      if (stepNumber <= PageFormIdEnum.timeSelection)
        newState = {
          ...newState,
          availableTimes: null,
          selectedDate: null,
          selectedTime: null,
          selectedDropoffOptionId: null,
          packagesUsedForTimeRequest: null
        };

      if (stepNumber <= PageFormIdEnum.contactInformation) {
        newState = {
          ...newState,
          contactInformation: null,
          campaignResponse: null,
          campaignValidationState: DiscountCodeValidationStateEnum.NotValidated
        };
      }

      if (stepNumber < PageFormIdEnum.summary) {
        newState = {
          ...newState,
          orderId: null,
          bookingError: null,
          isBookingMadeSuccessful: false
        };
      }

      return newState;
    }
  ),

  on(BookingActions.uploadFile, (state): BookingState => ({
    ...state,
    isPartialLoading: true
  })),

  on(BookingActions.uploadFileSuccess, (state, { fileId }): BookingState => ({
    ...state,
    isPartialLoading: false,
    fileIds: [...state.fileIds, fileId]
  })),

  on(BookingActions.uploadFileFailure, (state, { error }): BookingState => ({
    ...state,
    isPartialLoading: false,
    error: error
  })),

  on(BookingActions.clearFile, (state, { fileId }): BookingState => ({
    ...state,
    fileIds: state.fileIds.filter((id) => id !== fileId)
  })),

  // --- Dev Helpers ---
  on(
    BookingActions.hydrateBookingState,
    (state, { state: hydratedState }): BookingState => {
      // Deep merge the hydrated state with the current state
      // Convert JSON objects back to class instances
      const carInfo = hydratedState.carInfo
        ? VehicleSearchResponse.fromJS(hydratedState.carInfo)
        : state.carInfo;

      const techUpdateStatus = hydratedState.techUpdateStatus
        ? GetTechUpdateStatusResponse.fromJS(hydratedState.techUpdateStatus)
        : state.techUpdateStatus;

      const activeWorkshopSystems = hydratedState.activeWorkshopSystems
        ? hydratedState.activeWorkshopSystems.map((r) =>
            ActiveWorkshopSystemResponse.fromJS(r)
          )
        : state.activeWorkshopSystems;

      const dealers = hydratedState.dealers
        ? hydratedState.dealers.map((r) => DealerResponse.fromJS(r))
        : state.dealers;

      const basePackages = hydratedState.basePackages
        ? hydratedState.basePackages.map((r) => PackageResponse.fromJS(r))
        : state.basePackages;

      const insuranceProviders = hydratedState.insuranceProviders
        ? hydratedState.insuranceProviders.map((r) =>
            InsuranceCompanyResponse.fromJS(r)
          )
        : state.insuranceProviders;

      const warningLights = hydratedState.warningLights
        ? hydratedState.warningLights.map((r) =>
            VehicleWarningLightsResponse.fromJS(r)
          )
        : state.warningLights;

      const externalTyreUrl = hydratedState.externalTyreUrl
        ? ExternalTyreUrlResponse.fromJS(hydratedState.externalTyreUrl)
        : state.externalTyreUrl;

      const additionalPackages = hydratedState.additionalPackages
        ? hydratedState.additionalPackages.map((r) => PackageResponse.fromJS(r))
        : state.additionalPackages;

      const availableTimes = hydratedState.availableTimes
        ? hydratedState.availableTimes.map((r) =>
            CalendarDayTimeSlotResponse.fromJS(r)
          )
        : state.availableTimes;

      const campaignResponse = hydratedState.campaignResponse
        ? CampaignResponse.fromJS(hydratedState.campaignResponse)
        : state.campaignResponse;

      return {
        ...state,
        ...hydratedState,
        carInfo,
        techUpdateStatus,
        activeWorkshopSystems,
        dealers,
        basePackages,
        insuranceProviders,
        warningLights,
        externalTyreUrl,
        additionalPackages,
        availableTimes,
        campaignResponse,
        isLoading: false,
        isPartialLoading: false,
        error: null
      };
    }
  )
);
